import React, { SyntheticEvent, useEffect, useState } from 'react';
import { Heading } from '@uhg-abyss/web/ui/Heading';
import { TextInput } from '@uhg-abyss/web/ui/TextInput';
import { Text } from '@uhg-abyss/web/ui/Text';
import { DateInput } from '@uhg-abyss/web/ui/DateInput';
import { Button } from '@uhg-abyss/web/ui/Button';
import { VisuallyHidden } from '@uhg-abyss/web/ui/VisuallyHidden';
import { FormProvider } from '@uhg-abyss/web/ui/FormProvider';
import { useForm } from '@uhg-abyss/web/hooks/useForm';
import { Grid } from '@uhg-abyss/web/ui/Grid';
import { Layout } from '@uhg-abyss/web/ui/Layout';
import { Divider } from '@uhg-abyss/web/ui/Divider';
import { API } from '@src/api';
import { dobStringToObject } from '@src/utills/utils';
import { LoadingSpinner } from '@uhg-abyss/web/ui/LoadingSpinner';
import { IconSymbol } from '@uhg-abyss/web/ui/IconSymbol';
import * as data from 'advocate/.abyss/environments.json';
import { config } from '@uhg-abyss/web/tools/config';
import {
  HcpMemberSearchResponse,
  MemberSearchResult,
  UserInfo,
} from '@src/api.types';
import { useAtom } from 'jotai';
import { loggedInAdvocateAtom, memberInContextAtom } from '@src/global-state';
import { DisplayResults, memberSearchResultsAtom, NoSearchPerformed } from './DisplayResults';
import { saveFeatureFlagsToStorage } from '@src/utills/storage';
// Atom which encapsulates the results of the member search.

const TextBetweenLines = () => {
  return (
    <div style={{ backgroundColor: '#F7FCFD' }}>
      <Grid>
        <Grid.Col span={{ xs: 5.5, md: 5.7, lg: 5.9 }}>
          <Divider />
        </Grid.Col>
        <Grid.Col
          span={{ xs: 1, md: 0.6, lg: 0.2 }}
          style={{ textAlign: 'center', padding: '0px', paddingTop: '9px' }}
        >
          <span>OR</span>
        </Grid.Col>
        <Grid.Col span={{ xs: 5.5, md: 5.7, lg: 5.9 }}>
          <Divider />
        </Grid.Col>
      </Grid>
    </div>
  );
};

type MemberDetailsForm = {
  firstName: string;
  lastName: string;
  dob: string;
};

type MemberPlanForm = {
  memberId: string;
};

export const MemberLookup = () => {
  const [isLoading, setIsLoading] = React.useState(false);
  const formPersonalInfo = useForm({
    defaultValues: {
      firstName: '',
      lastName: '',
      dob: '',
    },
  });

  const formMemberId = useForm({
    defaultValues: {
      memberId: '',
    },
  });

  const [
    hasPersonalInfoFormBeenCompleted,
    setHasPersonalInfoFormBeenCompleted,
  ] = useState(false);

  const [hasMemberIdFormBeenCompleted, setHasMemberIdFormBeenCompleted] =
    useState(false);

  const [memberSearchResults, setMemberSearchResults] = useAtom(
    memberSearchResultsAtom
  );

  const [lastSearchType, setLastSearchType] = useState<'info' | 'plan'>('plan');

  const [loggedInAdvocate, setLoggedInAdvocate] = useAtom(loggedInAdvocateAtom);
  const [, setMemberInContext] = useAtom(memberInContextAtom);

  useEffect(() => {
    setMemberInContext(null);
  }, []);

  const fetchUserInfo = async () => {
    const resp = await API.getUserInfo();
    if (resp) {
      setLoggedInAdvocate(resp.data as UserInfo);
    }
    // add in member look up and redirect file
    saveFeatureFlagsToStorage(config('defaultFeatureFlags'));
    API.getFeatureFlags()
    .then((respFeatureFlags) => {
      if (
        respFeatureFlags &&
        respFeatureFlags.status === 'success' &&
        respFeatureFlags.data &&
        Object.keys(respFeatureFlags.data).length > 0
      ) {
        saveFeatureFlagsToStorage(respFeatureFlags.data);
      } else {
        const defaultFeatureFlags = config('defaultFeatureFlags');
        saveFeatureFlagsToStorage(defaultFeatureFlags);
      }
    })
    .catch((error) => {
      console.error('Error fetching feature flags:', error);
      const defaultFeatureFlags = config('defaultFeatureFlags');
      saveFeatureFlagsToStorage(defaultFeatureFlags);
    });

  };
  useEffect(() => {
    fetchUserInfo();
  }, []);

  const onPersonalInfoFormChange = () => {
    setHasMemberIdFormBeenCompleted(
      hasPersonalInfoFormBeenCompleted ||
        Object.values(formPersonalInfo.getValues()).every((v: string) => {
          return v && v.length > 0;
        })
    );
    if (hasMemberIdFormBeenCompleted) {
      formPersonalInfo.validate();
    }
  };

  const onMemberIdFormChange = () => {
    setHasMemberIdFormBeenCompleted(
      hasMemberIdFormBeenCompleted ||
        Object.values(formMemberId.getValues()).every((formField: string) => {
          return formField && formField.length > 0;
        })
    );
    if (hasMemberIdFormBeenCompleted) {
      formMemberId.validate();
    }
  };

  const onSubmit = async (data: MemberPlanForm | MemberDetailsForm) => {
    setIsLoading(true);

    // Null indicates error
    let result: MemberSearchResult[] | null = null;
    let resp: HcpMemberSearchResponse | null = null;

    if ('memberId' in data) {
      setLastSearchType('plan');
      const request = { memberId: data.memberId };
      resp = await API.searchMemberById(request);
    } else {
      setLastSearchType('info');
      const request = {
        firstName: data.firstName,
        lastName: data.lastName,
        dob: dobStringToObject(data.dob)!,
      };
      resp = await API.searchMemberByDetails(request);
    }

    if (
      resp !== null &&
      resp !== undefined &&
      'status' in resp &&
      resp.status === 'success'
    ) {
      // data will be an array of results. Empty array means no results were found.
      result = resp.data;
    } else {
      result = null;
    }

    setIsLoading(false);
    setMemberSearchResults(result);
  };

  const clearSearchResults = () => {
    setMemberSearchResults(undefined);
  };

  return (
    <React.Fragment>
      <div className="member-lookup-wrapper" style={{ paddingLeft: '5.5%', paddingRight: '5.5%', backgroundColor: '#F7FCFD' }}>
        {isLoading ? (
          <div
            style={{
              zIndex: 999,
              backgroundColor: '#FFFFFF',
              opacity: 0.75,
              position: 'fixed',
              width: '100%',
              height: '100%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              padding: 0,
              margin: 0,
              top: 0,
              left: 0,
            }}
          >
            <LoadingSpinner
              size="xl"
              isLoading={isLoading}
              ariaLoadingLabel="Downloading files"
            />
          </div>
        ) : (
          ``
        )}

        <style>{`
          .member-lookup-wrapper label {
            font-weight: 900 !important;
          }
        `}</style>
        <div style={{ marginTop: '20px' }}>
          <Heading serif>Welcome to UHC Community Connector</Heading>
        </div>

        <div style={{ backgroundColor: '#F7FCFD' }}>
          <FormProvider
            data-cy="member-info-form"
            state={formPersonalInfo}
            onSubmit={onSubmit}
            style={{}}
            css={{
              '.noAsterisk': {
                display: 'none',
              },
            }}
          >
            <br />
            <Heading size="md">
              Look up member by name & date of birth
            </Heading>
            <br />
            <Grid>
              <Grid.Col span={{ xs: 12, md: 6, lg: 3 }}>
                <TextInput
                  label={
                    <span>
                      <span>First name</span>{' '}
                      <span style={{ fontWeight: 'normal' }}>(required)</span>
                    </span>
                  }
                  placeholder=""
                  model="firstName"
                  validators={{ required: 'Please enter a valid name' }}
                  onChange={onPersonalInfoFormChange}
                  className="noAsterisk"
                />
              </Grid.Col>

              <Grid.Col span={{ xs: 12, md: 6, lg: 3 }}>
                <TextInput
                  label={
                    <span>
                      <span>Last name</span>{' '}
                      <span style={{ fontWeight: 'normal' }}>(required)</span>
                    </span>
                  }
                  placeholder=""
                  model="lastName"
                  // subText="Please enter a valid name"
                  validators={{ required: 'Please enter a valid name' }}
                  onChange={onPersonalInfoFormChange}
                />
              </Grid.Col>
              <Grid.Col span={{ xs: 12, md: 6, lg: 3 }}>
                <DateInput
                  model="dob"
                  subText=""
                  label={
                    <span>
                      <span>Date of birth</span>{' '}
                      <span style={{ fontWeight: 'normal' }}>(required)</span>
                    </span>
                  }
                  validators={{ required: 'Please enter a valid date' }}
                  onChange={onPersonalInfoFormChange}
                />
              </Grid.Col>
              <Grid.Col span={{ xs: 12, md: 6, lg: 3 }}>
                <Layout.Group
                  style={{
                    paddingTop: '25px',
                  }}
                >
                  <Button
                    type="submit"
                    isDisabled={!formPersonalInfo.formState.isValid}
                    aria-describedby="reason-disabled"
                  >
                    Search
                  </Button>
                  <VisuallyHidden id="reason-disabled">
                    The submit button is disabled because form fields have
                    invalid input.
                  </VisuallyHidden>
                  <Button
                    variant="outline"
                    isDisabled={!formPersonalInfo.formState.isValid}
                    onClick={(e: SyntheticEvent) => {
                      e.preventDefault();
                      formPersonalInfo.reset();
                      clearSearchResults();
                    }}
                  >
                    Clear
                  </Button>

                  <VisuallyHidden id="reason-disabled">
                    The clear button is disabled because form fields have not
                    been used.
                  </VisuallyHidden>
                </Layout.Group>
              </Grid.Col>
            </Grid>
            <br />
            <br />
          </FormProvider>

          <TextBetweenLines />
          <FormProvider
            data-cy="member-id-form"
            state={formMemberId}
            onSubmit={onSubmit}
          >
            <br />
            <Heading size="md">Look up member by ID</Heading>
            <br />
            <Grid>
              <Grid.Col span={{ xs: 12, md: 6, lg: 3 }}>
                <TextInput
                  label={
                    <span>
                      <span>Member ID</span>{' '}
                      <span style={{ fontWeight: 'normal' }}>(required)</span>
                    </span>
                  }
                  placeholder=""
                  model="memberId"
                  subText=""
                  isDisabled={false}
                  validators={{ required: 'Please enter a valid member ID' }}
                  onChange={onMemberIdFormChange}
                />
                <br />
                <p style={{ whiteSpace: 'nowrap' }}>
                  Types include Medicaid Subscriber ID, MBI ID and Medicare
                  Supplement ID
                </p>
              </Grid.Col>
              <Grid.Col span={{ xs: 12, md: 6, lg: 2 }}>
                <Layout.Group
                  style={{
                    paddingTop: '25px',
                  }}
                >
                  <Button
                    type="submit"
                    isDisabled={!formMemberId.formState.isValid}
                    aria-describedby="reason-disabled"
                  >
                    Search
                  </Button>
                  <VisuallyHidden id="reason-disabled">
                    The submit button is disabled because form fields have
                    invalid input.
                  </VisuallyHidden>
                  <Button
                    variant="outline"
                    isDisabled={!formMemberId.formState.isValid}
                    onClick={(e: SyntheticEvent) => {
                      e.preventDefault();
                      formMemberId.reset();
                      clearSearchResults();
                    }}
                  >
                    Clear
                  </Button>

                  <VisuallyHidden id="reason-disabled">
                    The clear button is disabled because form fields have not
                    been used.
                  </VisuallyHidden>
                </Layout.Group>
              </Grid.Col>
            </Grid>
            <br />
            <br />
          </FormProvider>
          <br />
        </div>
        <div style={{ paddingBottom: '0.5rem' }}>
          <Heading serif size="lg">Member search results</Heading>
          <br />

          {/* An array of results, even an empty one, means our search yield results */}
          {Array.isArray(memberSearchResults) && (
            <DisplayResults
              forSearchTerm={lastSearchType}
            />
          )}

          {/* Show instructions if search was unsuccessful or not performed yet */}
          {(memberSearchResults === undefined ||
            memberSearchResults === null) && (
            <div>
              <Text fontSize="md" color="$core.color.brand.100">
                Enter member information above to display results.
              </Text>
            </div>
          )}

          {/* Our results being null indicates that the API returned an error */}
          {memberSearchResults === null && (
            <div>
              <div
                style={{
                  padding: '24px',
                  marginTop: '15px',
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: '24px' }}>
                    <IconSymbol icon="error" color="$core.color.red.110" />
                    <Heading size="md" color="$core.color.red.110">
                      Member not found.
                    </Heading>
                </div>
                <Text fontSize="md" color="#002677" style={{ paddingLeft: '20px' }}>
                  <span style={{ display: 'inline-flex', alignItems: 'center' }}>
                    <span style={{ margin: '0 30px' }}>•</span>
                      Confirm you input the correct Member ID.
                  </span>
                </Text>
              </div>
              <div style={{ textAlign: 'center' }}>
                <Button
                  onClick={(e) => {
                    e.preventDefault();
                    setMemberSearchResults(undefined as NoSearchPerformed);
                  }}
                  variant="text"
                  color="brand"
                >
                  Search again
                  <IconSymbol
                    icon="arrow_upward"
                  />
                </Button>
              </div>
            </div>
          )}
        </div>
      </div>
    </React.Fragment>
  );
};
